import { createFileRoute } from "@tanstack/react-router";
import {
  Container,
  DirectAnswer,
  CtaBand,
  Eyebrow,
  Lead,
  PrimaryLink,
  Quote,
  TextLink,
} from "@/components/site/primitives";
import { SERVICES, SITE, TESTIMONIALS } from "@/lib/site-content";
import { ogImageMeta } from "@/lib/og";
import { RelatedLinks } from "@/components/site/RelatedLinks";
import { breadcrumbMeta } from "@/lib/internal-links";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { contactFunnel, trackOutboundClick } from "@/lib/analytics";
import { useSectionViews, type TrackedSection } from "@/hooks/useSectionViews";
import { AREAS, jsonLdScript, professionalServiceSchema, serviceSchema } from "@/lib/schema";

import { regionalHreflang } from "@/lib/hreflang";
import { RegionalLocationSwitcher } from "@/components/site/RegionalLocationSwitcher";

const PATH = "/fractional-cto-uk";
const CTA_SOURCE_HERO = "fractional_cto_uk_hero";
const CTA_SOURCE_BAND = "fractional_cto_uk_footer";

/** Sections reported as read, in page order, keyed by their heading id. */
const TRACKED_SECTIONS: TrackedSection[] = [
  { id: "fctouk-hero-heading", name: "Hero" },
  { id: "fctouk-definition-heading", name: "What a fractional CTO is" },
  { id: "fctouk-when-heading", name: "When founders bring me in" },
  { id: "fctouk-locations-heading", name: "Locations" },
  { id: "fctouk-services-heading", name: "What I do" },
  { id: "fctouk-quotes-heading", name: "Testimonials" },
  { id: "fctouk-faq-heading", name: "FAQ" },
];

const title = "Fractional CTO UK | Timothy Ng, Scryla";
const description =
  "Roadmap slipping or a tech team underperforming? Fractional CTO for UK startups and SMEs: technology audits, strategy, delivery assurance and hiring.";

const FAQ = [
  {
    q: "What is a fractional CTO?",
    a: "A fractional CTO is an experienced chief technology officer who works with your company part-time rather than as a full-time hire. You get senior technical judgement on the decisions that carry real cost, architecture, delivery, hiring, suppliers and spend, without the salary, equity and hiring risk of an executive appointment.",
  },
  {
    q: "What does a fractional CTO do day to day?",
    a: "Audits where your technology, team, spend and risk stand today; sets a technology plan that follows from the business plan, with sequence and cost named; holds in-house and outsourced development teams to what they promised; and coaches founders and technical leads so the capability stays in the business.",
  },
  {
    q: "Which parts of the UK do you cover?",
    a: "All of it, remote-first, with in-person time where a board or team needs it. I am based in Manchester, and Manchester to London is a two-hour train, so on-site days in the South East are straightforward to arrange.",
  },
  {
    q: "How much does a fractional CTO cost in the UK?",
    a: "There is no list price, and I would not trust one. A day of diligence on an existing build is a different commitment from a standing monthly seat that carries hiring and supplier decisions with it. UK engagements are quoted after a first conversation, against the scope we agree, so you can see what you are buying before anything is signed.",
  },
  {
    q: "How is a fractional CTO different from an interim CTO?",
    a: "An interim CTO fills a vacant full-time seat for a fixed period, usually most of the week. A fractional CTO is a permanent-but-part-time arrangement sized to the stage of the business, often one or two days a month rising when a programme needs it.",
  },
  {
    q: "How is a fractional CTO different from a non-executive director?",
    a: "A fractional CTO works inside the business, owning technical decisions and leading the team. A non-executive director sits outside it, holding the executive to account and giving the board independent assurance. I keep the two separate, and I will say which one you actually need.",
  },
];

const WHEN = [
  {
    title: "Your outsourced team has no one holding it to account",
    body: "Someone has to read what the development team promised against what they shipped. Without that, you are approving invoices on trust.",
  },
  {
    title: "You cannot yet justify a full-time CTO",
    body: "The technical decisions are already executive-level, but the headcount is not. Fractional covers the judgement without the salary, equity and hiring risk.",
  },
  {
    title: "You are raising, or being diligenced",
    body: "Investors put the technology under a spotlight. Knowing what they will find, before they find it, is worth more than a tidy deck.",
  },
  {
    title: "The board cannot tell if delivery is on track",
    body: "When progress reporting is filtered through the people doing the work, you need an unfiltered read on the roadmap, the team and the next twelve months of cost.",
  },
];

const LOCATIONS = [
  {
    title: "Manchester and the North West",
    body: "My base, and where most of my long-standing client relationships started. On-site time with founders and development teams is straightforward here.",
  },
  {
    title: "London and the South East",
    body: "Remote-first with in-person days for board meetings, investor sessions and delivery reviews. A two-hour train makes a monthly on-site rhythm practical without a London price attached to it.",
  },
  {
    title: "Rest of the UK, remote",
    body: "Written reports, scheduled calls and availability between them. The work has run this way for years, and the sectors I know well, fintech, digital health, iGaming, SaaS, data and agencies, are distributed anyway.",
  },
];

export const Route = createFileRoute("/fractional-cto-uk")({
  // Static marketing content: let the CDN serve it and revalidate in the background.
  headers: () => ({
    "cache-control": "public, max-age=0, s-maxage=3600, stale-while-revalidate=86400",
  }),
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE.url}${PATH}` },
      ...ogImageMeta("fractional-cto-uk", "Fractional CTO for UK founders and SMEs | Timothy Ng"),
    ],
    links: [{ rel: "canonical", href: `${SITE.url}${PATH}` }, ...regionalHreflang],
    scripts: [
      jsonLdScript(
        professionalServiceSchema({
          path: PATH,
          name: "Fractional CTO services | Timothy Ng, Scryla",
          areaServed: AREAS.uk,
          serviceType: "Fractional CTO",
        }),
      ),
      jsonLdScript(
        serviceSchema({
          path: PATH,
          serviceType: "Fractional CTO",
          areaServed: AREAS.uk,
        }),
      ),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((item) => ({
            "@type": "Question",
            name: item.q,
            acceptedAnswer: { "@type": "Answer", text: item.a },
          })),
        }),
      },
      breadcrumbMeta(PATH),
    ],
  }),
  component: FractionalCtoUkPage,
});

function FractionalCtoUkPage() {
  useSectionViews(PATH, TRACKED_SECTIONS);

  return (
    <div>
      <section
        aria-labelledby="fctouk-hero-heading"
        className="border-b border-border bg-gradient-to-b from-background to-surface"
      >
        <Container>
          <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <Breadcrumbs path={PATH} />
            <RegionalLocationSwitcher region="uk" />
          </div>
          <Eyebrow>Fractional CTO · UK</Eyebrow>
          <h1
            id="fctouk-hero-heading"
            className="mt-4 max-w-[22ch] text-[34px] leading-[1.06] font-bold tracking-[-0.035em] text-balance md:text-5xl"
          >
            Fractional CTO for UK startups and SMEs.
          </h1>
          <DirectAnswer className="mt-6">
            A fractional CTO is an experienced chief technology officer working with your company
            part-time rather than as a full-time hire. In the UK that typically means senior
            judgement on architecture, delivery, hiring, suppliers and spend, on a set cadence,
            without the salary, equity and search cost of an executive appointment.
          </DirectAnswer>
          <Lead className="mt-5">
            Roadmap slipping and no senior technologist accountable for it? Based in Manchester,
            working with founders and boards in London and across the UK. Former CTO and COO, and
            founder of my own consultancy since 2014, senior technical leadership on the decisions
            that matter, without a full-time executive hire.
          </Lead>
          <div className="mt-8 flex flex-wrap gap-3">
            <PrimaryLink to="/contact" onClick={() => contactFunnel.ctaClick(CTA_SOURCE_HERO)}>
              Arrange a conversation
            </PrimaryLink>
            <a
              href={SITE.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackOutboundClick("linkedin", CTA_SOURCE_HERO)}
              className="inline-flex items-center gap-2 rounded-lg border border-border-strong bg-background px-6 py-3.5 text-[15px] font-semibold transition-colors hover:border-primary hover:text-primary"
            >
              View my LinkedIn profile
            </a>
          </div>
        </Container>
      </section>

      <section className="border-b border-border" aria-labelledby="fctouk-definition-heading">
        <Container>
          <h2
            id="fctouk-definition-heading"
            className="max-w-[26ch] text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            What a fractional CTO is
          </h2>
          <div className="mt-6 max-w-[62ch] space-y-5 text-base leading-relaxed text-body md:text-[19px]">
            <p>
              A fractional CTO is an experienced chief technology officer working with your company
              part-time. You get the judgement of someone who has run technology at executive level,
              applied to the decisions that carry real cost, without the salary, equity and hiring
              risk of a full-time appointment.
            </p>
            <p>
              In practice that means architecture and delivery decisions, holding development teams
              to what they promised, technology spend, security and compliance, and being the person
              your board or investors can put a technical question to.
            </p>
            <p>
              I take on a small number of clients at a time, and I have no development team to keep
              busy and no supplier referrals to protect. If the work is not right for me, I will say
              so.
            </p>
          </div>
          <RelatedLinks
            className="mt-7"
            paths={[
              "/how-to-hire-a-fractional-cto",
              "/fractional-cto-vs-full-time-vs-agency",
              "/what-i-do",
            ]}
          />
        </Container>
      </section>

      <section className="border-b border-border bg-surface" aria-labelledby="fctouk-when-heading">
        <Container>
          <Eyebrow>When to hire one</Eyebrow>
          <h2
            id="fctouk-when-heading"
            className="mt-3 max-w-[24ch] text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            The moments UK founders call me
          </h2>
          <ul className="mt-8 grid gap-5 sm:grid-cols-2">
            {WHEN.map((item) => (
              <li key={item.title} className="rounded-lg border border-border bg-card p-6">
                <h3 className="text-lg font-semibold">{item.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-body">{item.body}</p>
              </li>
            ))}
          </ul>
        </Container>
      </section>

      <section className="border-b border-border" aria-labelledby="fctouk-locations-heading">
        <Container>
          <Eyebrow>Where I work</Eyebrow>
          <h2
            id="fctouk-locations-heading"
            className="mt-3 max-w-[26ch] text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            Manchester, London and remote across the UK
          </h2>
          <ul className="mt-8 grid gap-5 md:grid-cols-3">
            {LOCATIONS.map((item) => (
              <li key={item.title} className="rounded-lg border border-border bg-card p-6">
                <h3 className="text-lg font-semibold">{item.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-body">{item.body}</p>
              </li>
            ))}
          </ul>
          <p className="mt-7 max-w-[70ch] text-[15px] leading-relaxed text-body">
            Regulated sectors put a second constraint on every technology decision. For health
            businesses that is set out on{" "}
            <TextLink to="/fractional-cto-digital-health">
              Fractional CTO for digital health
            </TextLink>
            .
          </p>
          <RelatedLinks className="mt-7" paths={["/fractional-cto-manchester"]} />
          <p className="mt-4 text-[15px]">
            <TextLink to="/case-studies/digital-marketing-agency-platform-scale">
              Case study: scaling a search platform from 250,000 to 5 million keywords
            </TextLink>
          </p>
          <p className="mt-4 text-[15px] text-body">{SITE.officeHours}</p>
        </Container>
      </section>

      <section
        className="border-b border-border bg-surface"
        aria-labelledby="fctouk-services-heading"
      >
        <Container>
          <h2
            id="fctouk-services-heading"
            className="max-w-[24ch] text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            What you get
          </h2>
          <ul className="mt-8 grid gap-5 sm:grid-cols-2">
            {SERVICES.map((service) => (
              <li key={service.title} className="rounded-lg border border-border bg-card p-6">
                <h3 className="text-lg font-semibold">{service.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-body">{service.short}</p>
              </li>
            ))}
          </ul>
        </Container>
      </section>

      <section className="border-b border-border" aria-labelledby="fctouk-quotes-heading">
        <Container>
          <h2 id="fctouk-quotes-heading" className="sr-only">
            What UK founders say
          </h2>
          <div className="grid gap-5 md:grid-cols-2">
            <Quote item={TESTIMONIALS.milanShah} />
            <Quote item={TESTIMONIALS.aaronBiggs} />
          </div>
        </Container>
      </section>

      <section className="border-b border-border bg-surface" aria-labelledby="fctouk-faq-heading">
        <Container>
          <Eyebrow>Questions</Eyebrow>
          <h2
            id="fctouk-faq-heading"
            className="mt-3 text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            Fractional CTO FAQs
          </h2>
          <dl className="mt-8 divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
            {FAQ.map((item) => (
              <div key={item.q} className="px-6 py-6">
                <dt className="text-lg font-semibold text-ink">{item.q}</dt>
                <dd className="mt-2 max-w-[70ch] text-[15px] leading-relaxed text-body">
                  {item.a}
                </dd>
              </div>
            ))}
          </dl>
          <RelatedLinks
            className="mt-8"
            label="Other regions and board work"
            variant="cards"
            paths={[
              "/fractional-cto-us",
              "/fractional-cto-southeast-asia",
              "/technology-non-executive-director-uk",
            ]}
          />
        </Container>
      </section>

      <CtaBand
        heading="Need a fractional CTO in the UK?"
        body="Tell me about the business, the stage it is at, and what is worrying you. I will tell you plainly whether I am the right person, and what I would look at first."
        to="/contact"
        cta="Arrange a conversation"
        onCtaClick={() => contactFunnel.ctaClick(CTA_SOURCE_BAND)}
      />
    </div>
  );
}
